import React, { useState, useEffect } from 'react';
import { db, collection, onSnapshot, query, orderBy } from '../firebase';
import { ServiceSchedule } from '../types';
import { ServiceScheduleViewerModal } from '../components/ServiceScheduleViewerModal';
import { Calendar, Building2, Plus, Eye, Printer } from 'lucide-react';

export const ServiceSchedulesPage: React.FC = () => {
  const [schedules, setSchedules] = useState<ServiceSchedule[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string>('');

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'service_schedules'), (snapshot) => {
      const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ServiceSchedule));
      setSchedules(docs);
    });
    return () => unsub();
  }, []);

  const handleOpenViewer = (clientId?: string) => {
    setSelectedClientId(clientId || '');
    setIsModalOpen(true);
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 text-white p-8 rounded-3xl shadow-xl">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Calendar className="w-6 h-6 text-blue-300" />
            <h1 className="text-3xl font-black tracking-tight">Service Schedules Module</h1>
          </div>
          <p className="text-xs text-blue-200 font-medium max-w-xl">
            Access quarterly client maintenance schedules, review branch AC counts, technician assignments, and export schedules via Print, Excel, Download, or PDF.
          </p>
        </div>

        <button
          onClick={() => handleOpenViewer('')}
          className="px-6 py-3 bg-white text-blue-900 rounded-2xl font-bold hover:bg-blue-50 transition-all shadow-lg flex items-center gap-2 cursor-pointer text-xs uppercase tracking-wider"
        >
          <Eye className="w-4 h-4 text-blue-900" />
          <span>View All Service Schedules</span>
        </button>
      </div>

      {schedules.length > 0 ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-black text-slate-900">Generated Service Schedule List</h2>
            <span className="text-xs font-bold text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
              {schedules.length} Schedule(s) Available
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {schedules.map((schedule) => {
              const totalAcs = (schedule.items || []).reduce((acc, item) => acc + (item.noOfAcs || 0), 0);
              const branchCount = (schedule.items || []).length;
              const completedCount = (schedule.items || []).filter(i => i.status === 'COMPLETED').length;

              return (
                <div key={schedule.id} className="bg-white border border-slate-100 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="px-3 py-1 bg-indigo-50 text-indigo-800 border border-indigo-200 rounded-xl text-[10px] font-black uppercase tracking-wider">
                        {schedule.clientName || 'Client Schedule'}
                      </span>
                      <span className="text-[10px] text-slate-400 font-bold">
                        {schedule.scheduleDate || 'No Date'}
                      </span>
                    </div>

                    <h3 className="text-sm font-bold text-slate-900 line-clamp-2 leading-snug">
                      {schedule.quarterTitle}
                    </h3>

                    <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-100 text-center">
                      <div className="bg-slate-50 p-2 rounded-xl">
                        <p className="text-[9px] font-bold text-slate-400 uppercase">Branches</p>
                        <p className="text-xs font-black text-slate-800">{branchCount}</p>
                      </div>
                      <div className="bg-blue-50/60 p-2 rounded-xl">
                        <p className="text-[9px] font-bold text-blue-600 uppercase">Total ACs</p>
                        <p className="text-xs font-black text-blue-900">{totalAcs}</p>
                      </div>
                      <div className="bg-emerald-50/60 p-2 rounded-xl">
                        <p className="text-[9px] font-bold text-emerald-600 uppercase">Completed</p>
                        <p className="text-xs font-black text-emerald-900">{completedCount}/{branchCount}</p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-2">
                    <button
                      onClick={() => handleOpenViewer(schedule.clientId)}
                      className="flex-1 py-2 bg-indigo-900 hover:bg-indigo-800 text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 shadow-sm cursor-pointer"
                    >
                      <Eye className="w-3.5 h-3.5 text-indigo-200" />
                      <span>View Schedule</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="bg-white p-12 rounded-3xl border border-slate-200 shadow-sm text-center space-y-4">
          <Calendar className="w-16 h-16 text-indigo-200 mx-auto" />
          <h2 className="text-xl font-black text-slate-800">Client Service Schedules Viewer</h2>
          <p className="text-xs text-slate-500 max-w-md mx-auto">
            Click the button below to select a client and inspect all generated quarterly service schedules with full print and export capabilities.
          </p>
          <button
            onClick={() => handleOpenViewer('')}
            className="px-8 py-3.5 bg-blue-900 text-white rounded-2xl font-bold hover:bg-blue-800 transition-all shadow-lg shadow-blue-900/20 inline-flex items-center gap-2 text-xs uppercase tracking-wider cursor-pointer"
          >
            <Eye className="w-4 h-4" />
            <span>Open Service Schedules Viewer</span>
          </button>
        </div>
      )}

      <ServiceScheduleViewerModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        defaultClientId={selectedClientId}
      />
    </div>
  );
};

export default ServiceSchedulesPage;
